import React, {useContext, useEffect, useState} from "react";
import TrackVASContext from "../../../TrackVASContext";
import {PageTitle} from "../../../../_metronic/layout/core";
import {useLocation, useParams} from "react-router-dom";
import {deleteOrgPayment, getOrgPayments, recordOrgPaymentBatch} from "../../generic/crud";
import {showToast, TOAST_FAILURE, TOAST_SUCCESS} from "../../../utils/toast/Toast";
import {simpleDateFormat} from "../../../utils/Utils";
import {showConfirmDialog} from "../../../generic-components/ConfirmDialog";
import {RecordPaymentDialog} from "./RecordPaymentDialog";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_FULL_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function SAPaymentHistoryPage() {
    const myData = useContext(TrackVASContext);
    const {orgId} = useParams();
    const location = useLocation();
    const orgName = location.state?.orgName || "Organization";
    const monthlyAmount = location.state?.monthlyAmount || 0;
    const createdOn = location.state?.createdOn;

    const [payments, setPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [expandedMonth, setExpandedMonth] = useState(null);

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-indexed

    // Determine start year from org creation date
    const orgStartDate = createdOn ? new Date(createdOn) : new Date(currentYear, 0, 1);
    const startYear = orgStartDate.getFullYear();

    const profileBreadCrumbs = [
        {title: "Home", path: "/sadashboard", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
        {title: "Billing", path: "/super-admin/billing", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
    ];

    function loadData() {
        setIsLoading(true);
        getOrgPayments(orgId).then(({data}) => {
            setPayments(data);
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    }

    // Build a map of billingMonth -> list of payments
    function getPaymentMap() {
        const map = {};
        payments.forEach(p => {
            const key = p.billingMonth;
            if (key) {
                if (!map[key]) map[key] = [];
                map[key].push(p);
            }
        });
        return map;
    }

    function isMonthInRange(year, monthIdx) {
        const monthDate = new Date(year, monthIdx, 1);
        const orgStart = new Date(orgStartDate.getFullYear(), orgStartDate.getMonth(), 1);
        const now = new Date(currentYear, currentMonth, 1);
        return monthDate >= orgStart && monthDate <= now;
    }

    function formatMonthKey(year, monthIdx) {
        return `${year}-${String(monthIdx + 1).padStart(2, '0')}`;
    }

    function toggleMonthSelection(monthKey) {
        setSelectedMonths(prev => {
            if (prev.includes(monthKey)) {
                return prev.filter(m => m !== monthKey);
            } else {
                return [...prev, monthKey];
            }
        });
    }

    function openRecordPayment() {
        if (selectedMonths.length === 0) {
            showToast("Please select at least one unpaid month.", TOAST_FAILURE);
            return;
        }
        setShowModal(true);
    }

    async function handleDeletePayment(paymentId) {
        const result = await showConfirmDialog("Do you really want to delete this payment record?");
        if (result) {
            deleteOrgPayment(orgId, paymentId).then(() => {
                showToast("Payment record deleted!", TOAST_SUCCESS);
                setExpandedMonth(null);
                loadData();
            }).catch((err) => {
                showToast(err.response?.data || "Error deleting payment.", TOAST_FAILURE);
            });
        }
    }

    const handleClose = () => setShowModal(false);

    function handlePaymentSaved() {
        setSelectedMonths([]);
        setExpandedMonth(null);
        loadData();
    }

    useEffect(() => {
        myData.setBaseData({toolBarButtons: []});
        loadData();
        return () => {
            myData.setBaseData({toolBarButtons: []});
        };
    }, []);

    const paymentMap = getPaymentMap();
    const yearOptions = [];
    for (let y = startYear; y <= currentYear; y++) {
        yearOptions.push(y);
    }

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Payment History - {orgName}</PageTitle>

            {/* Summary Card */}
            <div className="card mb-5">
                <div className="card-body d-flex align-items-center justify-content-between py-5">
                    <div>
                        <span className="text-muted fw-semibold fs-7">Monthly Billing Total</span>
                        <div className="text-dark fw-bold fs-3">₹{monthlyAmount ? monthlyAmount.toLocaleString() : "0"}</div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        {selectedMonths.length > 0 && (
                            <div className="d-flex align-items-center gap-3">
                                <span className="badge badge-light-primary fs-7 px-4 py-2">
                                    {selectedMonths.length} month{selectedMonths.length > 1 ? 's' : ''} selected
                                </span>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={openRecordPayment}>
                                    <i className="fa fa-check me-1"></i>Record Payment
                                </button>
                                <button
                                    className="btn btn-light btn-sm"
                                    onClick={() => setSelectedMonths([])}>
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Calendar Card */}
            <div className="card card-body">
                {isLoading && <div className="text-center py-10"><span>Loading...</span></div>}
                {!isLoading && (
                    <>
                        {/* Year Navigation */}
                        <div className="d-flex align-items-center justify-content-center mb-8">
                            <button
                                className="btn btn-sm btn-icon btn-light"
                                disabled={selectedYear <= startYear}
                                onClick={() => setSelectedYear(selectedYear - 1)}>
                                <i className="fa fa-chevron-left"></i>
                            </button>
                            <h2 className="fw-bold mx-6 mb-0">{selectedYear}</h2>
                            <button
                                className="btn btn-sm btn-icon btn-light"
                                disabled={selectedYear >= currentYear}
                                onClick={() => setSelectedYear(selectedYear + 1)}>
                                <i className="fa fa-chevron-right"></i>
                            </button>
                        </div>

                        {/* Year Tabs */}
                        {yearOptions.length > 1 && (
                            <div className="d-flex justify-content-center gap-2 mb-6">
                                {yearOptions.map(y => (
                                    <button
                                        key={y}
                                        className={`btn btn-sm ${y === selectedYear ? 'btn-primary' : 'btn-light'}`}
                                        onClick={() => setSelectedYear(y)}>
                                        {y}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Month Grid */}
                        <div className="row g-4">
                            {MONTH_NAMES.map((monthName, monthIdx) => {
                                const monthKey = formatMonthKey(selectedYear, monthIdx);
                                const inRange = isMonthInRange(selectedYear, monthIdx);
                                const monthPayments = paymentMap[monthKey] || [];
                                const isPaid = monthPayments.length > 0;
                                const isSelected = selectedMonths.includes(monthKey);
                                const isExpanded = expandedMonth === monthKey;

                                return (
                                    <div key={monthKey} className="col-lg-3 col-md-4 col-sm-6">
                                        <div
                                            className={`card border h-100 ${!inRange ? 'opacity-25' : ''}`}
                                            style={{
                                                cursor: inRange ? 'pointer' : 'default',
                                                border: isSelected ? '2px solid #009ef7 !important' : undefined,
                                                boxShadow: isSelected ? '0 0 10px rgba(0, 158, 247, 0.3)' : undefined,
                                            }}>
                                            <div
                                                className="card-body p-4 text-center"
                                                onClick={() => {
                                                    if (!inRange) return;
                                                    if (isPaid) {
                                                        setExpandedMonth(isExpanded ? null : monthKey);
                                                    } else {
                                                        toggleMonthSelection(monthKey);
                                                    }
                                                }}>

                                                {/* Status indicator */}
                                                <div className="mb-2">
                                                    {inRange && isPaid && (
                                                        <span className="badge badge-light-success px-4 py-2 fs-8">
                                                            <i className="fa fa-check-circle text-success me-1"></i>PAID
                                                        </span>
                                                    )}
                                                    {inRange && !isPaid && !isSelected && (
                                                        <span className="badge badge-light-danger px-4 py-2 fs-8">
                                                            <i className="fa fa-times-circle text-danger me-1"></i>UNPAID
                                                        </span>
                                                    )}
                                                    {inRange && !isPaid && isSelected && (
                                                        <span className="badge badge-primary px-4 py-2 fs-8">
                                                            <i className="fa fa-check me-1"></i>SELECTED
                                                        </span>
                                                    )}
                                                    {!inRange && (
                                                        <span className="badge badge-light px-4 py-2 fs-8">-</span>
                                                    )}
                                                </div>

                                                {/* Month name */}
                                                <h4 className="fw-bold mb-1">{monthName}</h4>
                                                <span className="text-muted fs-8">{selectedYear}</span>

                                                {/* Amount for paid months */}
                                                {inRange && isPaid && (
                                                    <div className="mt-2">
                                                        <span className="text-success fw-bold fs-7">
                                                            ₹{monthPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
                                                        </span>
                                                        {monthPayments.length > 1 && (
                                                            <span className="text-muted fs-9 d-block">
                                                                ({monthPayments.length} payments)
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Expanded payment details */}
                                            {isExpanded && isPaid && (
                                                <div className="card-footer bg-light-success p-3">
                                                    {monthPayments.map((p, idx) => (
                                                        <div key={p.id} className={`d-flex justify-content-between align-items-start ${idx > 0 ? 'mt-2 pt-2 border-top' : ''}`}>
                                                            <div>
                                                                <div className="fw-bold fs-7">₹{p.amount?.toLocaleString()}</div>
                                                                {p.referenceNumber && (
                                                                    <div className="text-muted fs-8">Ref: {p.referenceNumber}</div>
                                                                )}
                                                                {p.remarks && (
                                                                    <div className="text-muted fs-9">{p.remarks}</div>
                                                                )}
                                                                <div className="text-muted fs-9">
                                                                    by {p.recordedBy} on {p.createdOn ? simpleDateFormat(p.createdOn) : "-"}
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="btn btn-sm btn-icon btn-light-danger ms-2"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeletePayment(p.id);
                                                                }}>
                                                                <i className="fa fa-trash-alt fs-7"></i>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Legend */}
                        <div className="d-flex justify-content-center gap-5 mt-8">
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge badge-light-success px-3 py-1">PAID</span>
                                <span className="text-muted fs-8">Payment recorded</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge badge-light-danger px-3 py-1">UNPAID</span>
                                <span className="text-muted fs-8">Click to select</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge badge-primary px-3 py-1">SELECTED</span>
                                <span className="text-muted fs-8">Ready to record</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <RecordPaymentDialog
                show={showModal}
                handleClose={handleClose}
                orgId={orgId}
                monthlyAmount={monthlyAmount}
                selectedMonths={selectedMonths}
                onSave={handlePaymentSaved}
            />
        </>
    );
}
